import { clsx } from "clsx";
import { forwardRef, useCallback } from "react";
import { FocusRing, mergeProps, useHover, usePress } from "react-aria";
import { useDispatch } from "react-redux";

import { Menu, MenuItem, MenuSection, SymbolIcon, useMenu } from "widgets";
import { useDOMRef } from "widgets/hooks";

import style from "@/modules/core/styles/Tile.module.scss";

import { sortAlpha } from "widgets/utils";
import { getIconElementForPlaybook } from "../../../../app-utils/playbook.js";
import type { Playbook } from "../../../../graphql.js";
import { openPlaybook } from "../../../../redux/modules/playbook-execution.js";
import type { ReduxDispatch } from "../../../../redux/redux-utils.js";
import { ProtectedResourceLogo } from "../../ProtectedResourceLogo.js";

interface PlaybookLauncherTileProps {
  playbook: Playbook;
}

const PlaybookLauncherTile = forwardRef<HTMLDivElement, PlaybookLauncherTileProps>(
  function PlaybookLauncherTile(props, ref) {
    const dispatch = useDispatch<ReduxDispatch>();

    const { hoverProps, isHovered } = useHover({});

    const { pressProps } = usePress({
      onPress: () => dispatch(openPlaybook(props.playbook)),
    });

    const Icon = useCallback(
      () => <img src={getIconElementForPlaybook(props.playbook).large} alt={"Playbook icon"} />,
      [props.playbook],
    );

    if (!props.playbook) return null;

    return (
      <FocusRing focusRingClass={style.tile_focus}>
        <div
          className={clsx(style.playbook_launcher, { [style.tile_hover]: isHovered })}
          ref={ref}
          {...mergeProps(hoverProps, pressProps)}
          role="button"
          tabIndex={0}
        >
          {props.playbook.standard && <ProtectedResourceLogo size={24} className={style.selector_icon} />}
          <Icon />
          <div className={style.tile_content} style={{ textAlign: "center" }}>
            <div className={style.title}>{props.playbook.name}</div>
            <div className={style.description}>{props.playbook.description}</div>
          </div>
        </div>
      </FocusRing>
    );
  },
);

interface PlaybookLauncherTileAsMenuProps {
  playbooks: Playbook[];
}

const PlaybookLauncherTileAsMenu = forwardRef<HTMLDivElement, PlaybookLauncherTileAsMenuProps>(
  function PlaybookLauncherTile(props, ref) {
    const dispatch = useDispatch<ReduxDispatch>();

    const { hoverProps, isHovered } = useHover({});

    const Icon = useCallback(
      () => <img src={getIconElementForPlaybook(props.playbooks[0]).large} alt={"Playbook icon"} />,
      [props.playbooks],
    );

    const tRef = useDOMRef(ref);
    const { triggerRef, menuRef, menuProps, triggerProps, state, arrowProps } = useMenu<HTMLDivElement, HTMLDivElement>(
      {
        placement: "bottom",
        offset: -8,
      },
      tRef,
    );

    const { pressProps } = usePress({
      onPress: () => {
        state.open();
      },
    });

    if (!props.playbooks) return null;

    return (
      <>
        <FocusRing focusRingClass={style.tile_focus}>
          <div
            className={clsx(style.playbook_launcher, style.tile_playbook_group, { [style.tile_hover]: isHovered })}
            ref={triggerRef}
            {...mergeProps(hoverProps, triggerProps, pressProps)}
            role="button"
            tabIndex={0}
          >
            {props.playbooks.every((p) => p.standard) && (
              <ProtectedResourceLogo size={24} className={style.selector_icon} />
            )}
            <SymbolIcon icon={"menu"} className={style.group_icon} />
            <Icon />
            <div className={style.tile_content} style={{ textAlign: "center" }}>
              <div className={style.title}>{props.playbooks[0].name}</div>
              <div className={style.description}>{props.playbooks.length} Playbooks</div>
            </div>
          </div>
        </FocusRing>
        {state.isOpen && (
          <Menu domProps={menuProps} arrowProps={arrowProps} ref={menuRef}>
            <MenuSection id={"playbooks"} title={"Playbooks"} hideTitle>
              {props.playbooks
                .sort((p1, p2) => sortAlpha(p1.nameSuffix, p2.nameSuffix))
                .map((playbook) => (
                  <MenuItem
                    id={playbook.id}
                    key={playbook.id}
                    onAction={() => dispatch(openPlaybook(playbook))}
                    label={playbook.nameSuffix ?? `(${playbook.name})`}
                    description={playbook.description}
                    iconImage={{ path: getIconElementForPlaybook(playbook).small, width: 18, height: 18 }}
                  />
                ))}
            </MenuSection>
          </Menu>
        )}
      </>
    );
  },
);

export { PlaybookLauncherTile, PlaybookLauncherTileAsMenu };
